/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import Dropdown from 'bootstrap/js/dist/dropdown';
import * as XLSX from 'xlsx';
import {
  getPaginatedNotifications,
  createNotification,
  createNotificationWithAi,
  updateNotification,
  deleteNotification,
  getNotificationById,
  approveNotification,
} from '../api/notificationsAPI';
import {
  getPaginatedAssets,
  getPaginatedAssetsAndAreas,
  getAssetByAssetNumber,
  getAssetById,
} from '../api/assetAPI';
import { getAreaById } from '../api/areaAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import { validateForm } from '../utils/useValidator';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useSetting } from '../utils/AppSettingsContext';
import { getUsersById } from '../api/usersAPI';
import { showToastSuccess } from '../utils/errorController';
import NotificationPrintView from './NotificationPrintView';
import exportNotificationPdf from '../utils/exportNotificationPdf';
import NotificationFormModal from './notification/NotificationFormModal';
import { buildNotificationFields } from './notification/notificationFormUtils';

const NotificationsLayer = ({ viewMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newNotification, setNewNotification] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [formReadOnly, setFormReadOnly] = useState(false);
  const [selectedAssign, setSelectedAssign] = useState(null);
  const [imageModalSrc, setImageModalSrc] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiPromptText, setAiPromptText] = useState('');
  const [aiError, setAiError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const panelTableRef = useRef(null);
  const panelContainerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const tableRef = useRef(null);
  const [panelNotifications, setPanelNotifications] = useState([]);
  const [panelPage, setPanelPage] = useState(1);
  const [panelHasMore, setPanelHasMore] = useState(true);
  const [panelLoading, setPanelLoading] = useState(false);
  const panelTabs = useMemo(
    () => [
      { value: 'open', label: 'Open', statuses: [4] },
      { value: 'approved', label: 'Approved', statuses: [5, 2] },
    ],
    [],
  );
  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);
  const [activePanelTab, setActivePanelTab] = useState('open');
  const [selectedNotificationItem, setSelectedNotificationItem] = useState(null);
  const [panelReloadKey, setPanelReloadKey] = useState(0);
  const [approvedByName, setApprovedByName] = useState('');

  const [tableNotifications, setTableNotifications] = useState([]);
  const [tablePage, setTablePage] = useState(1);
  const [tableHasMore, setTableHasMore] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [tableReloadKey, setTableReloadKey] = useState(0);
  const [showApproveConfirmation, setShowApproveConfirmation] = useState(false);
  const [notificationPendingApproval, setNotificationPendingApproval] = useState(null);
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [notificationPendingRejection, setNotificationPendingRejection] = useState(null);
  const userProfile = useUserProfile();
  const workOrderPrefix = useSetting('work_order_number_prefix');
  const shiftStartTime = useSetting('shift_start_time');

  const handleAssetSearch = async (inputValue) => {
    try {
      const shouldLoadDefault = !inputValue;
      if (!shouldLoadDefault && inputValue.length < 2) return [];
      const resp = await getPaginatedAssets({ page: 1, pageSize: 10, search: shouldLoadDefault ? '' : inputValue });
      const items = resp.data || resp;
      return items.map((a) => ({
        value: a.id,
        label: `${a.assetNumber} – ${a.assetDescription}`,
        assetDepartmentId: a.assetDepartmentId,
      }));
    } catch (err) {
      console.error('Error fetching assets:', err);
      return [];
    }
  };

  const ASSIGN_PAGE_SIZE = 25;

  const handleAssignSearch = useCallback(
    async (inputValue, { page } = {}) => {
      const raw = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
      const trimmed = raw.trim();
      const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
      const shouldLoadDefault = trimmed === '';

      if (!shouldLoadDefault && trimmed.length < 2) {
        return { options: [], hasMore: false };
      }

      try {
        const resp = await getPaginatedAssetsAndAreas({
          page: pageNumber,
          pageSize: ASSIGN_PAGE_SIZE,
          search: shouldLoadDefault ? '' : trimmed,
          type: 'all',
        });
        const items = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        const options = items.map((i) => ({ value: i.id, label: i.label, type: i.type }));
        const totalCount =
          typeof resp?.total === 'number'
            ? resp.total
            : typeof resp?.count === 'number'
              ? resp.count
              : undefined;
        const hasMore =
          typeof resp?.hasMore === 'boolean'
            ? resp.hasMore
            : typeof totalCount === 'number'
              ? pageNumber * ASSIGN_PAGE_SIZE < totalCount
              : options.length === ASSIGN_PAGE_SIZE;

        return {
          options,
          totalCount,
          pageSize: ASSIGN_PAGE_SIZE,
          hasMore,
        };
      } catch (err) {
        console.error('Error fetching assets/areas:', err);
        return { options: [], hasMore: false };
      }
    },
    [],
  );

  const formatAssignOption = (option) => (
    <div className="d-flex justify-content-start align-items-center gap-2">
      <span className="badge bg-secondary ms-2">
        <Icon
          icon={option.type === 'Asset' ? 'mage:delivery-truck' : 'ic:baseline-location-on'}
          className="icon text-xl line-height-1"
        />
      </span>
      <span>{option.label}</span>
    </div>
  );

  const formatPriorityOption = (option) => (
    <div className="d-flex align-items-center gap-2">
      <span
        className={`d-inline-block rounded-circle bg-${option.colour || 'secondary'}`}
        style={{ width: '10px', height: '10px' }}
      ></span>
      <span>{option.label}</span>
    </div>
  );


  const handleImageClick = (src) => {
    setImageModalSrc(src);
    setShowImageModal(true);
  };

  useEffect(() => {
    if (!selectedAssign) {
      setNewNotification((prev) => ({
        ...prev,
        assignable: null,
        assetId: null,
        areaId: null,
      }));
      return;
    }
    if (selectedAssign.type === 'Asset') {
      setNewNotification((prev) => ({
        ...prev,
        assignable: selectedAssign.value,
        assetId: selectedAssign.value,
        areaId: null,
      }));
      getAssetById(selectedAssign.value)
        .then((asset) => {
          if (asset?.assetDepartmentId) {
            setNewNotification((prev) => ({
              ...prev,
              departmentId: asset.assetDepartmentId,
            }));
          }
        })
        .catch((err) => console.error('Error loading asset department', err));
    } else {
      setNewNotification((prev) => ({
        ...prev,
        assignable: selectedAssign.value,
        areaId: selectedAssign.value,
        assetId: null,
      }));
      getAreaById(selectedAssign.value)
        .then((area) => {
          if (area?.departmentId) {
            setNewNotification((prev) => ({
              ...prev,
              departmentId: area.departmentId,
            }));
          }
        })
        .catch((err) => console.error('Error loading area department', err));
    }
  }, [selectedAssign]);

  const notificationFields = useMemo(
    () =>
      buildNotificationFields({
        newNotification,
        selectedAssign,
        setSelectedAssign,
        handleAssignSearch,
        formatAssignOption,
        formatPriorityOption,
        formReadOnly,
        editingId,
        onImageClick: handleImageClick,
      }),
    [
      newNotification,
      selectedAssign,
      handleAssignSearch,
      formatAssignOption,
      formatPriorityOption,
      formReadOnly,
      editingId,
    ],
  );

  const tableFilters = useMemo(
    () => [
      { field: 'notificationStatusId', type: 'dropDown', label: 'Status', api: 'notification-status', displayField: 'name' },
      { field: 'priorityId', type: 'dropDown', label: 'Priority', api: 'priority', displayField: 'name' },
      { field: 'departmentId', type: 'dropDown', label: 'Department', api: 'hierarchy-structures', displayField: 'name', menuBarFilter: 1 },
      { field: 'teamsId', type: 'dropDown', label: 'Team', api: 'teams', displayField: 'teamName', menuBarFilter: 1 },
      { field: 'areaId', type: 'dropDown', label: 'Area', api: 'areas', displayField: 'name' },
      { field: 'assetId', type: 'paginatedDropdown', label: 'Asset', loadOptions: handleAssetSearch },
    ],
    []
  );

  const { renderField } = useFormBuilder(notificationFields, newNotification, setNewNotification, errors);

  const resetTablePagination = () => {
    setTableNotifications([]);
    setTablePage(1);
    setTableHasMore(true);
    setTableLoading(false);
    setTableReloadKey((key) => key + 1);
  };

  const panelResetPendingRef = useRef(false);

  const resetPanelPagination = () => {
    panelResetPendingRef.current = true;
    setPanelNotifications([]);
    setPanelPage(1);
    setPanelHasMore(true);
    setPanelLoading(false);
  };

  const handleSortChange = (field) => {
    resetTablePagination();
    if (sortField === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortField(null);
        setSortOrder(null);
      } else {
        setSortOrder('asc');
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field || !sortOrder) {
      return 'mdi:sort';
    }
    return sortOrder === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending';
  };

  const renderSortableHeader = (label, field) => {
    const ariaSort = sortField === field
      ? sortOrder === 'asc'
        ? 'ascending'
        : sortOrder === 'desc'
          ? 'descending'
          : 'none'
      : 'none';

    return (
      <th
        onClick={() => handleSortChange(field)}
        style={{ cursor: 'pointer' }}
        scope="col"
        aria-sort={ariaSort}
      >
        <span className="d-inline-flex align-items-center gap-1">
          {label}
          <Icon className="text-xl" icon={getSortIcon(field)} />
        </span>
      </th>
    );
  };

  const updateTable = () => {
    resetTablePagination();

    resetPanelPagination();
    // Ensure no stale selection remains when refreshing the panel list
    setSelectedNotificationItem(null);
    setPanelReloadKey((k) => k + 1);
  };

  const {
    appliedFilters,
    appliedFilterParams,
    renderFilterControls,
    setTableKeyLocal,
    tableKey
  } = useTableFilters(
    tableFilters,
    { notificationStatusId: [4, 5, 2] },
    {
      onReset: () => {
        setSearchTerm('');
        updateTable();
      },
      onApply: updateTable,
    }
  );

  useEffect(() => {
    updateTable();
  }, [viewMode, appliedFilters, appliedFilterParams, searchTerm]);

  useEffect(() => {
    setSelectedNotificationItem(null);
    resetPanelPagination();
    setPanelReloadKey((key) => key + 1);
  }, [activePanelTab]);

  useEffect(() => {
    if (viewMode !== 'table' || tableLoading || !tableHasMore) return;

    const fetchTable = async () => {
      setTableLoading(true);
      try {
        const firstPageSize = 25;
        const subsequentPageSize = 5;
        const pageSize = tablePage === 1 ? firstPageSize : subsequentPageSize;
        const page = tablePage === 1
          ? 1
          : tablePage + (firstPageSize / subsequentPageSize) - 1;
        const resp = await getPaginatedNotifications({
          page,
          pageSize,
          search: searchTerm,
          priorityId: appliedFilterParams.priorityId,
          notificationStatusId: appliedFilterParams.notificationStatusId,
          teamsId: appliedFilterParams.teamsId,
          locationId: appliedFilterParams.locationId,
          assetId: appliedFilterParams.assetId?.map((o) => o.value),
          departmentId: appliedFilterParams.departmentId,
          areaId: appliedFilterParams.areaId,
          onlyWithTeamsAssigned: appliedFilterParams.teamsId?.length ? 1 : undefined,
          sortField: sortField || undefined,
          sortOrder: sortOrder || undefined,
        });
        setTableNotifications((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newItems = resp.data.filter((item) => !existingIds.has(item.id));
          const combined = [...prev, ...newItems];
          setTableHasMore(combined.length < resp.total);
          return combined;
        });
      } catch (err) {
        console.error('Error fetching table notifications:', err);
      } finally {
        setTableLoading(false);
      }
    };

    fetchTable();
  }, [tablePage, viewMode, appliedFilterParams, searchTerm, sortField, sortOrder, tableReloadKey]);

  useEffect(() => {
    if (viewMode !== 'table') return;
    const container = tableContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 5 &&
        tableHasMore &&
        !tableLoading
      ) {
        setTablePage((p) => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [viewMode, tableHasMore, tableLoading, tableKey]);

  useEffect(() => {
    if (viewMode !== 'panel' || panelLoading || !panelHasMore) return;

    if (panelResetPendingRef.current && panelPage !== 1) {
      return;
    }

    if (panelResetPendingRef.current) {
      panelResetPendingRef.current = false;
    }

    const fetchPanel = async () => {
      const currentTab = panelTabs.find((tab) => tab.value === activePanelTab);
      const tabStatuses = currentTab?.statuses || [];
      const appliedStatuses = Array.isArray(appliedFilterParams.notificationStatusId)
        ? appliedFilterParams.notificationStatusId
        : [];
      const statusFilter = appliedStatuses.length
        ? tabStatuses.filter((statusId) => appliedStatuses.includes(statusId))
        : tabStatuses;

      if (statusFilter.length === 0) {
        setPanelNotifications([]);
        setPanelHasMore(false);
        setPanelLoading(false);
        return;
      }

      setPanelLoading(true);
      try {
        const firstPageSize = 25;
        const subsequentPageSize = 5;
        const pageSize = panelPage === 1 ? firstPageSize : subsequentPageSize;
        const page = panelPage === 1
          ? 1
          : panelPage + (firstPageSize / subsequentPageSize) - 1;
        const resp = await getPaginatedNotifications({
          page,
          pageSize,
          search: searchTerm,
          priorityId: appliedFilterParams.priorityId,
          notificationStatusId: statusFilter,
          teamsId: appliedFilterParams.teamsId,
          locationId: appliedFilterParams.locationId,
          assetId: appliedFilterParams.assetId?.map((o) => o.value),
          departmentId: appliedFilterParams.departmentId,
          areaId: appliedFilterParams.areaId,
          onlyWithTeamsAssigned: appliedFilterParams.teamsId?.length ? 1 : undefined,
          sortField: sortField || undefined,
          sortOrder: sortOrder || undefined,
        });
        setPanelNotifications((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newItems = resp.data.filter((item) => !existingIds.has(item.id));
          const combined = [...prev, ...newItems];
          if (!selectedNotificationItem && combined.length > 0) {
            setSelectedNotificationItem(combined[0]);
          }
          setPanelHasMore(combined.length < resp.total);
          return combined;
        });
      } catch (err) {
        console.error('Error fetching panel notifications:', err);
      } finally {
        setPanelLoading(false);
      }
    };

    fetchPanel();
  }, [panelPage, viewMode, appliedFilterParams, searchTerm, panelReloadKey, sortField, sortOrder, activePanelTab, panelTabs]);

  useEffect(() => {
    const container = panelContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 5 &&
        panelHasMore &&
        !panelLoading
      ) {
        setPanelPage((p) => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [panelHasMore, panelLoading, viewMode]);

  useEffect(() => {
    const loadUserName = async () => {
      if (selectedNotificationItem?.approvedBy) {
        try {
          const user = await getUsersById(selectedNotificationItem.approvedBy);
          setApprovedByName(user.name || `User ${selectedNotificationItem.approvedBy}`);
        } catch (err) {
          console.error('Error fetching approving user:', err);
          setApprovedByName(`User ${selectedNotificationItem.approvedBy}`);
        }
      } else {
        setApprovedByName('');
      }
    };
    loadUserName();
  }, [selectedNotificationItem?.approvedBy]);


  const handleView = (data) => {
    setFormReadOnly(true);
    const assignOpt = data.asset
      ? {
        value: data.asset.id,
        label: `${data.asset.assetNumber} – ${data.asset.assetDescription}`,
        type: 'Asset',
      }
      : data.area
        ? { value: data.area.id, label: data.area.name, type: 'Location' }
        : null;
    setSelectedAssign(assignOpt);
    setNewNotification({
      ...data,
      workOrderNumber: data.tblWorkOrders?.workOrderNumber || '',
      assignable: assignOpt ? assignOpt.value : null,
    });
    setEditingId(data.id);
    setShowModal(true);
  };

  const handleEdit = (data) => {
    setFormReadOnly(!!data.workOrderId);
    const assignOpt = data.asset
      ? {
        value: data.asset.id,
        label: `${data.asset.assetNumber} – ${data.asset.assetDescription}`,
        type: 'Asset',
      }
      : data.area
        ? { value: data.area.id, label: data.area.name, type: 'Location' }
        : null;
    setSelectedAssign(assignOpt);
    setNewNotification({
      ...data,
      workOrderNumber: data.tblWorkOrders?.workOrderNumber || '',
      assignable: assignOpt ? assignOpt.value : null,
    });
    setEditingId(data.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete notification?')) {
      await deleteNotification(id);
      setTableNotifications((prev) => prev.filter((n) => n.id !== id));
      setPanelNotifications((prev) => prev.filter((n) => n.id !== id));
      setSelectedNotificationItem((prev) => (prev?.id === id ? null : prev));
      updateTable();
    }
  };

  const generateCsv = () => {
    const headers = [
      'Number',
      'Title',
      'Status',
      'Priority',
      'Asset/Area',
      'Department',
      'Team',
      'Work Order',
    ];

    const rows = tableNotifications.map((row) => [
      row.notificationNumber,
      row.notificationTitle,
      row.notificationStatus?.name || '',
      row.priority?.name || '',
      row.assetId
        ? `${row.asset?.assetNumber || ''} - ${row.asset?.assetDescription || ''}`
        : row.areaId
          ? `${row.area?.code || row.area?.areaCode || ''} - ${row.area?.name || ''}`
          : '',
      row.department?.name || '',
      row.tblTeams?.teamName || '',
      row.tblWorkOrders?.workOrderNumber || '',
    ]);

    const csvLines = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = [
      'Number',
      'Title',
      'Status',
      'Priority',
      'Asset/Area',
      'Department',
      'Team',
      'Work Order',
    ];

    const rows = tableNotifications.map((row) => [
      row.notificationNumber,
      row.notificationTitle,
      row.notificationStatus?.name || '',
      row.priority?.name || '',
      row.assetId
        ? `${row.asset?.assetNumber || ''} - ${row.asset?.assetDescription || ''}`
        : row.areaId
          ? `${row.area?.code || row.area?.areaCode || ''} - ${row.area?.name || ''}`
          : '',
      row.department?.name || '',
      row.tblTeams?.teamName || '',
      row.tblWorkOrders?.workOrderNumber || '',
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Notifications');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'notifications.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'notifications.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearForm = () => {
    setNewNotification({});
    setSelectedAssign(null);
    setEditingId(null);
    setFormReadOnly(false);
    setErrors({});
  };

  const openAiModal = () => {
    setAiPromptText('');
    setAiError('');
    setShowAiModal(true);
  };

  const closeAiModal = () => {
    if (!aiLoading) {
      setShowAiModal(false);
    }
  };

  const handleAiCreateNotification = async () => {
    if (!aiPromptText.trim()) {
      setAiError('Please provide notification details.');
      return;
    }
    setAiLoading(true);
    setAiError('');
    try {
      const response = await createNotificationWithAi(aiPromptText.trim());
      const aiData = response?.data;
      if (!aiData) {
        setAiError(response?.message || 'Unable to create notification with AI.');
        return;
      }

      const assetNumber = aiData.assetNumber?.trim();
      if (!assetNumber) {
        setAiError('AI response is missing an asset number.');
        return;
      }

      const asset = await getAssetByAssetNumber(assetNumber);
      if (!asset?.id) {
        setAiError(`Unable to find asset with number "${assetNumber}".`);
        return;
      }

      setNewNotification({
        notificationNumber: 'Auto-generated on save',
        notificationTitle: aiData.notificationTitle ?? '',
        notificationDescription: aiData.notificationDescription ?? '',
        priorityId: Number(aiData.priority ?? 3),
        notificationStatusId: Number(aiData.notificationStatusId ?? 4),
      });
      setSelectedAssign({
        value: asset.id,
        label: `${asset.assetNumber || assetNumber} – ${asset.assetDescription || ''}`.trim(),
        type: 'Asset',
      });
      setEditingId(null);
      setFormReadOnly(false);
      setErrors({});
      setShowAiModal(false);
      setShowModal(true);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to create notification with AI.';
      setAiError(message);
    } finally {
      setAiLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    clearForm();
  };

  const syncNotificationInLists = (notification, { prepend = false } = {}) => {
    if (!notification) return;

    const replaceInList = (list) => {
      const index = list.findIndex((item) => item.id === notification.id);
      if (index === -1) {
        return prepend ? [notification, ...list] : [...list, notification];
      }
      const updated = [...list];
      updated[index] = notification;
      return updated;
    };

    setPanelNotifications((prev) => replaceInList(prev));
    setTableNotifications((prev) => replaceInList(prev));
    setSelectedNotificationItem((prev) => (prev?.id === notification.id ? notification : prev));
  };

  const handleSave = async () => {
    const errs = validateForm(notificationFields, newNotification);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    try {
      const cleanPayload = {};
      for (const key in newNotification) {
        if (
          ['workOrderNumber', 'notificationStatusId', 'departmentId', 'notificationNumber'].includes(key)
        )
          continue;
        if (newNotification[key] !== null && typeof newNotification[key] !== 'object') {
          cleanPayload[key] = newNotification[key];
        }
      }

      const payload = {
        ...cleanPayload,
        assetId: selectedAssign?.type === 'Asset' ? selectedAssign.value : null,
        areaId: selectedAssign?.type === 'Location' ? selectedAssign.value : null,
        notificationImages: newNotification.notificationImages,
        notificationStatusId: 4,
        departmentId: newNotification.departmentId,
      };
      delete payload.assignable;
      let saved;
      if (editingId) {
        saved = await updateNotification(editingId, payload);
      } else {
        saved = await createNotification(payload);
      }
      const fullRecord = await getNotificationById(saved.id);
      syncNotificationInLists(fullRecord, { prepend: !editingId });
      setSelectedNotificationItem(fullRecord);
      closeModal();
    } catch (err) {
      console.error('Error saving notification:', err);
    }
  };

  const handleReject = async (notification) => {
    if (!notification?.id) return;
    try {
      await updateNotification(notification.id, { notificationStatusId: 3 });
      const refreshed = await getNotificationById(notification.id);
      syncNotificationInLists(refreshed);
      showToastSuccess('Work request rejected.');
      updateTable();
    } catch (err) {
      console.error('Error rejecting notification:', err);
    }
  };

  const openRejectConfirmation = (notification) => {
    setNotificationPendingRejection(notification);
    setShowRejectConfirmation(true);
  };

  const closeRejectConfirmation = () => {
    setShowRejectConfirmation(false);
    setNotificationPendingRejection(null);
  };

  const confirmReject = async () => {
    if (!notificationPendingRejection) return;
    try {
      await handleReject(notificationPendingRejection);
    } finally {
      closeRejectConfirmation();
    }
  };

  const handleConvert = async (selectedNotification) => {
    try {
      const linkedNotificationId = selectedNotification?.id ?? editingId;
      if (!linkedNotificationId) return;

      const response = await approveNotification({
        notificationId: linkedNotificationId,
        workOrderPrefix: workOrderPrefix || 'WO-',
        shiftStartTime: shiftStartTime || undefined,
        // workOrderfiles: notificationFields?.files,
      });

      const { workOrder, notification } = response || {};

      if (!workOrder || !notification) {
        console.error('Unexpected approval response:', response);
        return;
      }

      syncNotificationInLists(notification);
      setNewNotification((prev) => ({
        ...prev,
        ...notification,
        workOrderNumber: workOrder.workOrderNumber,
        workOrderId: workOrder.id,
      }));
      setFormReadOnly(true);
      showToastSuccess(
        `Notification approved and new work order number ${workOrder.workOrderNumber} created.`
      );
      // Force the panel list to refresh and select the first available record
      setSelectedNotificationItem(null);
      updateTable();
    } catch (err) {
      console.error('Error converting to work order:', err);
    }
  };

  const openApproveConfirmation = (notification) => {
    setNotificationPendingApproval(notification);
    setShowApproveConfirmation(true);
  };

  const closeApproveConfirmation = () => {
    setShowApproveConfirmation(false);
    setNotificationPendingApproval(null);
  };

  const confirmApprove = async () => {
    if (!notificationPendingApproval) return;
    try {
      await handleConvert(notificationPendingApproval);
    } finally {
      closeApproveConfirmation();
    }
  };

  const handleExportPdf = (notification) => {
    exportNotificationPdf(notification, approvedByName);
  };

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setTableKeyLocal();
            }}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center border-0 pb-16">
          <div className="d-flex flex-wrap align-items-top gap-2 ms-3">
            {renderFilterControls()}
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto me-8">
            <div className="dropdown me-2">
              <button
                className="btn btn-outline-secondary text-sm py-12 px-16 radius-4 dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Export Data
              </button>
              <ul className="dropdown-menu p-12 border bg-base shadow">
                <li>
                  <button className="dropdown-item" onClick={handleExportExcel}>
                    Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportCSV}>
                    CSV
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div>
            {userProfile?.privileges?.includes('notifications:update') && (
              <div className="btn-group">
                <button
                  onClick={() => {
                    setFormReadOnly(false);
                    setSelectedAssign(null);
                    setEditingId(null);
                    setErrors({});
                    setNewNotification({
                      notificationNumber: 'Auto-generated on save',
                      priorityId: 3,
                    });
                    setShowModal(true);
                  }}
                  className="btn btn-primary text-sm py-12 px-16 radius-4"
                >
                  <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                  Add Work Request
                </button>
                <button
                  type="button"
                  className="btn btn-primary text-sm dropdown-toggle dropdown-toggle-split"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="visually-hidden">Toggle Dropdown</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li className="my-6">
                    <button
                      className="dropdown-item py-8 d-flex align-items-center gap-2"
                      onClick={(e) => {
                        const toggleBtn = e.currentTarget
                          .closest('.btn-group')
                          ?.querySelector('.dropdown-toggle');
                        if (toggleBtn) {
                          Dropdown.getOrCreateInstance(toggleBtn).hide();
                        }
                        openAiModal();
                      }}
                    >
                      <Icon icon="mdi:stars-outline" className="icon text-xl line-height-1" />
                      Add With AI
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="card-body p-0">
          {viewMode === 'table' && (
            <div
              className="table-responsive flex-grow-1 overflow-auto scroll rounded-0"
              style={{ maxHeight: 'calc(100vh - 210px)' }}
              key={`table-${tableKey}`}
              ref={tableContainerRef}
            >
              <table className="table bordered-table mb-3 w-100 rounded-0" ref={tableRef}>
                <thead>
                  <tr>
                    <th className="rounded-0"></th>
                    {renderSortableHeader('Number', 'number')}
                    {renderSortableHeader('Title', 'title')}
                    {renderSortableHeader('Status', 'status')}
                    {renderSortableHeader('Priority', 'priority')}
                    {renderSortableHeader('Asset', 'asset')}
                    {renderSortableHeader('Department', 'department')}
                    <th>Team</th>
                    {renderSortableHeader('Work Order', 'workorderat')}
                    <th className="rounded-0">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tableNotifications.length === 0 && !panelLoading && (
                    <tr>
                      <td colSpan="10" className="text-center py-4 rounded-0">No Work Requests</td>
                    </tr>
                  )}
                  {tableNotifications.map((row) => {
                    const imgSrc = Array.isArray(row.notificationImages) && row.notificationImages.length
                      ? `${process.env.REACT_APP_SPACES_BASE}/${row.notificationImages[0]}`
                      : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                    const statusColour = row.notificationStatus?.colour?.bootstrapName || 'secondary';
                    const priorityColour = row.priority?.colours?.bootstrapName || 'secondary';
                    const teamInitials = row.tblTeams?.teamName
                      ? row.tblTeams.teamName
                        .split(' ')
                        .slice(0, 2)
                        .map((w) => w[0])
                        .join('')
                        .toUpperCase()
                      : '';
                    return (
                      <tr key={row.id}>
                        <td>
                          <img
                            src={imgSrc}
                            style={{
                              width: '45px',
                              height: '45px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '1px solid #ddd',
                            }}
                            alt="Notification"
                          />
                        </td>
                        <td>{row.notificationNumber}</td>
                        <td>{row.notificationTitle}</td>
                        <td>
                          {row.notificationStatus && (
                            <span className={`bg-${statusColour}-focus text-${statusColour}-main px-16 py-2 rounded-pill fw-medium text-sm`}>
                              {row.notificationStatus.name}
                            </span>
                          )}
                        </td>
                        <td>
                          {row.priority && (
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className={`d-inline-block rounded-1 bg-${priorityColour}`}
                                style={{ width: '10px', height: '10px' }}
                              ></span>
                              {row.priority.name}
                            </div>
                          )}
                        </td>
                        <td>
                          {row.assetId
                            ? `${row.asset?.assetNumber || ''} | ${row.asset?.assetDescription || ''}`
                            : row.areaId
                              ? `${row.area?.code || row.area?.areaCode || ''} - ${row.area?.name || ''}`
                              : ''}
                        </td>
                        <td>{row.department?.name || ''}</td>
                        <td>
                          {row.tblTeams ? (
                            <div
                              className={`rounded-circle bg-${row.tblTeams.colours?.bootstrapName} d-flex align-items-center justify-content-center text-white fw-bold`}
                              style={{ width: '35px', height: '35px', fontSize: '12px' }}
                            >
                              {teamInitials}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {row.tblWorkOrders?.workOrderNumber && (
                            <span className="badge bg-primary">{row.tblWorkOrders.workOrderNumber}</span>
                          )}
                        </td>
                        <td>
                          <button
                            className="view-btn w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleView(row)}
                          >
                            <Icon icon="ic:baseline-visibility" />
                          </button>
                          {userProfile?.privileges?.includes('notifications:update') && (
                            <button
                              className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6"
                              onClick={() => handleEdit(row)}
                            >
                              <Icon icon="ic:baseline-edit" />
                            </button>
                          )}
                          {userProfile?.privileges?.includes('notifications:update') && (
                            <button
                              className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              onClick={() => handleDelete(row.id)}
                            >
                              <Icon icon="ic:baseline-delete-outline" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {tableLoading && (
                <div className="text-center py-2">
                  <span className="spinner-border spinner-border-sm" />
                </div>
              )}
            </div>
          )}

          {viewMode === 'panel' && (
            <div className="row pb-16 pe-16" style={{ height: 'calc(100vh - 212px)' }}>
              {/* Left-hand side Table: Scrolls Independently */}
              <div className="col-md-5 h-100 d-flex flex-column ps-32">
                <ul className="nav bordered-tab border border-top-0 border-start-0 border-end-0 d-inline-flex nav-pills mb-16" role="tablist">
                  {panelTabs.map((tab) => (
                    <li className="nav-item" role="presentation" key={tab.value}>
                      <button
                        type="button"
                        className={`nav-link ${activePanelTab === tab.value ? 'active' : ''}`}
                        onClick={() => {
                          if (activePanelTab !== tab.value) {
                            setActivePanelTab(tab.value);
                          }
                        }}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>
                <div
                  className="table-responsive flex-grow-1 overflow-auto scroll"
                  style={{ border: 'solid #eee 2px', borderRadius: '8px' }}
                  key={`panel-${tableKey}`}
                  ref={panelContainerRef}
                >
                  <table
                    className="table table-hover mb-0 panel-table"
                    ref={panelTableRef}
                  >
                    <thead>
                      <tr>
                        <th style={{ display: 'none' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {panelNotifications.length === 0 && !panelLoading && (
                        <tr className='border-bottom'>
                          <td className="text-center py-4 rounded-0">No Work Requests</td>
                        </tr>
                      )}
                      {panelNotifications.map((row) => {
                        const assetImg = row.asset
                          ? `${process.env.REACT_APP_SPACES_BASE}/${row.asset.assetImage}`
                          : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                        const priority = row.priority?.name || '';
                        const asset = row.assetId
                          ? `${row.asset?.assetNumber} - ${row.asset?.assetDescription}` || ''
                          : row.areaId
                            ? row.area?.name || ''
                            : '';
                        const priorityColor = row.priority?.colours?.bootstrapName || 'secondary';
                        return (
                          <tr
                            key={row.id}
                            onClick={() => setSelectedNotificationItem(row)}
                            className={
                              selectedNotificationItem?.id === row.id
                                ? 'notification-selected'
                                : ''
                            }
                          >
                            <td>
                              <div className="d-flex gap-2 align-items-start justify-content-between">
                                <div className="d-flex gap-2 align-items-start">
                                  <img
                                    src={assetImg}
                                    style={{
                                      width: '45px',
                                      height: '45px',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: '1px solid #ddd',
                                    }}
                                    alt="Asset"
                                  />
                                  <div>
                                    <div className="fw-bold">
                                      {row.notificationNumber} – {row.notificationTitle}
                                    </div>
                                    <div className="text-muted text-sm">{asset ? asset : '-'}</div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        className={`d-inline-block rounded-1 bg-${priorityColor}`}
                                        style={{ width: '10px', height: '10px' }}
                                      ></span>
                                      {priority}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-sm text-end">
                                  <span
                                    className={`bg-${row.notificationStatus?.colour.bootstrapName || 'secondary'}-focus text-${row.notificationStatus?.colour.bootstrapName || 'secondary'}-main px-16 py-2 rounded-pill fw-medium text-sm`}
                                  >
                                    {row.notificationStatus?.name || ''}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-md-7 h-100 d-flex flex-column">
                {selectedNotificationItem && (
                  <div
                    className="card flex-grow-1 d-flex flex-column overflow-hidden"
                    style={{ border: 'solid #eee 2px' }}
                  >
                    <div className="card-header flex-shrink-0 card border-0">
                      <div className="row align-items-center">
                        <div className="col-8 d-flex align-items-center gap-2">
                          <h5 className="text-xl mb-0">
                            #{selectedNotificationItem.notificationNumber} - {selectedNotificationItem.notificationTitle}
                          </h5>
                        </div>
                        <div className="col-4 d-flex justify-content-end gap-2">
                          {userProfile?.privileges?.includes('notifications:approve') &&
                            !selectedNotificationItem.workOrderNumber &&
                            (selectedNotificationItem.notificationStatus?.name === 'Open' ||
                              selectedNotificationItem.notificationStatusId === 4) && (
                              <>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => openRejectConfirmation(selectedNotificationItem)}
                                  disabled={Boolean(selectedNotificationItem.workOrderId)}
                                >
                                  {/*<Icon
                                    icon="mdi:close"
                                    className="text-xl me-6"
                                    style={{ position: 'relative', top: '-1px' }}
                                  />*/}
                                  Reject
                                </button>
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() => openApproveConfirmation(selectedNotificationItem)}
                                  disabled={Boolean(selectedNotificationItem.workOrderId)}
                                >
                                  {/*<Icon
                                    icon="mdi:check"
                                    className="text-xl me-6"
                                    style={{ position: 'relative', top: '-1px' }}
                                  />*/}
                                  Approve
                                </button>
                              </>
                            )}
                          <div className="dropdown">
                            <button
                              type="button"
                              className="btn text-primary-600 hover-text-primary px-18 show border-0 text-xxl py-4"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="mdi:dots-vertical"
                                className='text-primary'
                                style={{ position: 'relative', top: '-5px' }}
                              />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end mt-12">
                              {userProfile?.privileges?.includes('notifications:update') && (
                                <li>
                                  <button
                                    type="button"
                                    className="dropdown-item py-12"
                                    onClick={() => handleEdit(selectedNotificationItem)}
                                  >
                                    Edit Work Request
                                  </button>
                                </li>
                              )}
                              <li>
                                <button
                                  type="button"
                                  className="dropdown-item py-12"
                                  onClick={() => handleExportPdf(selectedNotificationItem)}
                                >
                                  Export to PDF
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body overflow-auto flex-grow-1">
                      <NotificationPrintView
                        notification={selectedNotificationItem}
                        approvedByName={approvedByName}
                        handleImageClick={handleImageClick}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <NotificationFormModal
          show={showModal}
          title={editingId ? newNotification.notificationTitle : 'Add Work Request'}
          onClose={closeModal}
          onSave={handleSave}
          bodyTop={(
            <>
              {newNotification.workOrderNumber && (
                <div className="mb-3">
                  <span className="badge bg-success-100 text-md text-success">
                    Linked to Work Order {newNotification.workOrderNumber}
                  </span>
                </div>
              )}
              {editingId &&
                !newNotification.workOrderNumber &&
                userProfile?.privileges?.includes('notifications:approve') && (
                  <div className="d-flex justify-content-start mb-3">
                    <button
                      className="btn btn-outline-success btn-sm"
                      onClick={() => openApproveConfirmation(newNotification)}
                      disabled={Boolean(newNotification.workOrderId)}
                    >
                      <Icon icon="mdi:check" className="text-xl me-6" style={{ position: 'relative', top: '-1px' }} />
                      Approve
                    </button>
                  </div>
                )}
            </>
          )}
        >
          {notificationFields.map(renderField)}
        </NotificationFormModal>
      )}

      {showApproveConfirmation && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Approve Work Request</h5>
                <button type="button" className="btn-close" onClick={closeApproveConfirmation} />
              </div>
              <div className="modal-body">
                Do you want to approve this work request and create a new work order?
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeApproveConfirmation}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={confirmApprove}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectConfirmation && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject Work Request</h5>
                <button type="button" className="btn-close" onClick={closeRejectConfirmation} />
              </div>
              <div className="modal-body">Do you want to reject this Work Request?</div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeRejectConfirmation}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={confirmReject}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImageModal && (
        <div className="modal modal-backdrop show d-block" onClick={() => setShowImageModal(false)} tabIndex="-1">
          <div className="modal-dialog" style={{ maxWidth: '75%' }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <img src={imageModalSrc} alt="view" style={{ width: '100%' }} />
            </div>
          </div>
        </div>
      )}

      {showAiModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Icon className="text-xxl me-12 text-primary" style={{ position: 'relative', top: '-4px' }} icon="mdi:stars-outline" />
                  Create a Work Request
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeAiModal}
                  disabled={aiLoading}
                />
              </div>
              <div className="modal-body">
                <div className="text-sm text-secondary">
                  <p>
                    Use this to create a new work request using plain text. To ensure the work
                    request can be reviewed and raised correctly, please include the following
                    details where applicable.
                  </p>
                  <ul
                    style={{
                      listStyleType: 'disc',
                      listStylePosition: 'outside',
                    }}
                    className="ps-24"
                  >
                    <li>Asset number or asset name</li>
                    <li>A clear description of the issue or request</li>
                    <li>Priority or urgency if relevant</li>
                    <li>Any safety, compliance, or access considerations</li>
                  </ul>
                  <br />
                  <p>
                    Providing complete and clear information helps ensure the notification is created
                    accurately and actioned without delay.
                  </p>
                </div>

                {aiError && (
                  <div className="alert alert-danger" role="alert">
                    {aiError}
                  </div>
                )}
                <textarea
                  className="form-control"
                  rows={6}
                  value={aiPromptText}
                  onChange={(e) => setAiPromptText(e.target.value)}
                  disabled={aiLoading}
                  placeholder="Describe the work request you want to create"
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={closeAiModal}
                  disabled={aiLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAiCreateNotification}
                  disabled={aiLoading || !aiPromptText.trim()}
                >
                  {aiLoading ? 'Creating...' : 'Add With AI'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationsLayer;
